import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: credentials.username,
        password: credentials.password
      });
      
      localStorage.setItem('admin_token', response.data.access_token);
      
      const token = response.data.access_token;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const role = payload.role || 'client';
      
      if (role === 'client' || role === 'partner') {
        localStorage.setItem('client_token', response.data.access_token);
        navigate('/client/dashboard');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
            alt="Journeys N Tales" 
            className="login-logo"
          />
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Journeys N Tales</p>
        </div>

        {error && (
          <div className="form-error" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form" data-testid="login-form">
          <div className="form-field">
            <Label htmlFor="username">Email</Label>
            <div className="input-with-icon">
              <User size={18} className="input-icon" />
              <Input
                id="username"
                name="username"
                type="email"
                value={credentials.username}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                data-testid="login-email"
              />
            </div>
          </div>

          <div className="form-field">
            <Label htmlFor="password">Password</Label>
            <div className="input-with-icon">
              <Lock size={18} className="input-icon" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={credentials.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                data-testid="toggle-password"
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>

          <Button type="submit" className="login-button" disabled={loading} data-testid="login-submit">
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>

        <div className="auth-footer" style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <p style={{ color: '#718096', fontSize: '0.9rem' }}>
            Don't have an account? <Link to="/client/register" style={{ color: '#b8976c', fontWeight: '500', textDecoration: 'none' }}>Create Account</Link>
          </p>
        </div>

        <div className="login-footer">
          <p>Admin & Staff: Use your assigned credentials</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
